import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts, createProduct, updateProduct, deleteProduct, fetchProductDetails } from '../store/slices/productSlice';
import { fetchCategories } from '../store/slices/categorySlice';
import { RootState, AppDispatch } from '../store';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';
import { productAPI } from '../services/productAPI';

interface ProductFormData {
  name: string;
  description: string;
  price: string;
  discountPrice: string;
  category: string;
  stock: string;
  images: FileList | null;
  size: string;
  diamention: string;
  height: string;
  material: string;
  variants: Array<{
    size: number;
    images: FileList | null;
  }>;
}

const Products: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { products = [], totalProducts = 0, currentPage = 1, totalPages = 0, isLoading, selectedProduct } = useSelector((state: RootState) => state.products);
  const { categories = [] } = useSelector((state: RootState) => state.categories);
  
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    price: '',
    discountPrice: '',
    category: '',
    stock: '',
    images: null,
    size: '',
    diamention: '',
    height: '',
    material: '',
    variants: [{ size: 0, images: null }]
  });

  useEffect(() => {
    dispatch(fetchProducts({ page, limit: 10 }));
    dispatch(fetchCategories());
  }, [dispatch, page]);

  useEffect(() => {
    if (search || category || minPrice || maxPrice) {
      console.log('Applying filters:', { search, category, minPrice, maxPrice });
      const params: any = { page: 1, limit: 10 };
      if (search) params.search = search;
      if (category) params.categoryId = category; // Backend expects categoryId
      if (minPrice) params.minPrice = minPrice;
      if (maxPrice) params.maxPrice = maxPrice;
      dispatch(fetchProducts(params));
      setPage(1);
    }
  }, [search, category, minPrice, maxPrice, dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    
    formDataToSend.append('productname', formData.name);
    formDataToSend.append('productdetails', formData.description); // Backend expects productdetails
    formDataToSend.append('description', formData.description);
    formDataToSend.append('price', formData.price);
    formDataToSend.append('discountPrice', formData.discountPrice || '0');
    formDataToSend.append('stock', formData.stock || '0');
    formDataToSend.append('producttype', 'standard');
    formDataToSend.append('rating', '5');
    
    // Create variants structure with proper image counts and existing images
    const variants = formData.variants.map((variant, index) => {
      if (editingProduct && !variant.images) {
        // Keep existing images if no new images selected
        return {
          size: variant.size,
          imageCount: 0,
          images: editingProduct.variants?.[index]?.images || []
        };
      }
      return {
        size: variant.size,
        imageCount: variant.images ? variant.images.length : 0
      };
    });
    formDataToSend.append('variants', JSON.stringify(variants));
    
    // Create feature structure
    const feature = {
      diamention: formData.diamention,
      height: formData.height,
      material: formData.material
    };
    formDataToSend.append('feature', JSON.stringify(feature));
    
    // Append all variant images
    formData.variants.forEach(variant => {
      if (variant.images) {
        Array.from(variant.images).forEach((file) => {
          formDataToSend.append('variantImages', file);
        });
      }
    });

    try {
      if (editingProduct) {
        await dispatch(updateProduct({ id: editingProduct._id, data: formDataToSend })).unwrap();
        toast.success('Product updated successfully');
      } else {
        if (!formData.category) {
          toast.error('Please select a category');
          return;
        }
        await dispatch(createProduct({ productData: formDataToSend, categoryId: formData.category })).unwrap();
        toast.success('Product created successfully');
      }
      handleCloseModal();
      // Refresh the products list to get updated data
      dispatch(fetchProducts({ page, limit: 10 }));
    } catch (error: any) {
      toast.error(error.message || 'Operation failed');
    }
  };

  const handleEdit = (product: any) => {
    setEditingProduct(product);
    setFormData({
      name: product.productname || product.name || '',
      description: product.description || '',
      price: product.price?.toString() || '',
      discountPrice: product.discountPrice?.toString() || '',
      category: product.categoryId?._id || product.categoryId || '',
      stock: product.stock?.toString() || '',
      images: null,
      size: product.variants?.[0]?.size?.toString() || '',
      diamention: product.feature?.diamention || '',
      height: product.feature?.height || '',
      material: product.feature?.material || '',
      variants: product.variants?.map((v: any) => ({ size: v.size, images: null })) || [{ size: 0, images: null }]
    });
    setShowModal(true);
  };

  const handleViewProduct = async (productId: string) => {
    try {
      await dispatch(fetchProductDetails(productId)).unwrap();
      setShowProductModal(true);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch product details');
    }
  };

  const handleCloseProductModal = () => {
    setShowProductModal(false);
  };

  const handleDelete = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await dispatch(deleteProduct(productId)).unwrap();
        toast.success('Product deleted successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete product');
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      discountPrice: '',
      category: '',
      stock: '',
      images: null,
      size: '',
      diamention: '',
      height: '',
      material: '',
      variants: [{ size: 0, images: null }]
    });
  };

  const addVariant = () => {
    setFormData({
      ...formData,
      variants: [...formData.variants, { size: 0, images: null }]
    });
  };

  const removeVariant = (index: number) => {
    if (formData.variants.length > 1) {
      const newVariants = formData.variants.filter((_, i) => i !== index);
      setFormData({ ...formData, variants: newVariants });
    }
  };

  const updateVariant = (index: number, field: 'size' | 'images', value: any) => {
    const newVariants = [...formData.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setFormData({ ...formData, variants: newVariants });
  };

  const handleClearFilters = () => {
    setSearch('');
    setCategory('');
    setMinPrice('');
    setMaxPrice('');
    setPage(1);
    dispatch(fetchProducts({ page: 1, limit: 10 }));
  };

  const handleDownloadCSV = async () => {
    try {
      const params: any = { csv: 'true' };
      if (search) params.search = search;
      if (category) params.categoryId = category;
      if (minPrice) params.minPrice = minPrice;
      if (maxPrice) params.maxPrice = maxPrice;
      
      const response = await productAPI.downloadCSV(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('CSV downloaded successfully');
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products Management</h1>
          <p className="mt-2 text-sm text-gray-700">Manage all products</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
          <div>
            <input
              type="text"
              placeholder="Search products..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((cat: any) => (
                <option key={cat._id} value={cat._id}>{cat.categoryname}</option>
              ))}
            </select>
          </div>
          <div>
            <input
              type="number"
              placeholder="Min Price"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              min="0"
            />
          </div>
          <div>
            <input
              type="number"
              placeholder="Max Price"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              min="0"
            />
          </div>
          <div>
            <button
              type="button"
              onClick={handleClearFilters}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Clear Filters
            </button>
          </div>
          <div>
            <button
              onClick={handleDownloadCSV}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Download CSV
            </button>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Products ({totalProducts})
          </h3>
        </div>
        
        {isLoading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading products...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <p>No products found.</p>
            <p className="text-sm mt-1">Try adjusting your search filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product: any) => (
                  <tr key={product._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-16 w-16 flex-shrink-0">
                          <img
                            className="h-16 w-16 rounded-lg object-cover"
                            src={product.variants?.[0]?.images?.[0] ? `https://rudraksh-api.volvrit.org/${product.variants[0].images[0]}` : '/placeholder.jpg'}
                            alt={product.productname || product.name}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{product.productname || product.name}</div>
                          <div className="text-sm text-gray-500">{product.description?.substring(0, 50)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.categoryId?.categoryname || product.category?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <span className="font-medium">₹{product.price}</span>
                        {product.discountPrice && (
                          <div className="text-xs text-green-600">₹{product.discountPrice} (Sale)</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {product.stock || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        product.isactive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {product.isactive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleViewProduct(product._id)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-green-600 hover:text-green-900"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(product._id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setPage}
          isLoading={isLoading}
        />
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {editingProduct ? 'Edit Product' : 'Add New Product'}
                    </h3>
                    <button type="button" onClick={handleCloseModal}>
                      <XMarkIcon className="h-6 w-6 text-gray-400" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Product Name</label>
                      <input
                        type="text"
                        required
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <textarea
                        required
                        rows={3}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Price</label>
                        <input
                          type="number"
                          required
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={formData.price}
                          onChange={(e) => setFormData({...formData, price: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Discount Price</label>
                        <input
                          type="number"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={formData.discountPrice}
                          onChange={(e) => setFormData({...formData, discountPrice: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Category</label>
                        <select
                          required
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={formData.category}
                          onChange={(e) => setFormData({...formData, category: e.target.value})}
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat: any) => (
                            <option key={cat._id} value={cat._id}>{cat.categoryname}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Stock</label>
                        <input
                          type="number"
                          required
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={formData.stock}
                          onChange={(e) => setFormData({...formData, stock: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {editingProduct ? 'Current Images' : 'Product Images'}
                      </label>
                      {editingProduct && editingProduct.variants?.[0]?.images?.length > 0 && (
                        <div className="mt-2 mb-3">
                          <div className="grid grid-cols-4 gap-2">
                            {editingProduct.variants[0].images.map((image: string, index: number) => (
                              <img
                                key={index}
                                className="w-full h-16 object-cover rounded border"
                                src={`https://rudraksh-api.volvrit.org/${image}`}
                                alt={`Current ${index + 1}`}
                              />
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Current product images</p>
                        </div>
                      )}
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onChange={(e) => setFormData({...formData, images: e.target.files})}
                      />
                      {editingProduct && (
                        <p className="text-xs text-gray-500 mt-1">Leave empty to keep current images</p>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Dimension</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g., 10x10"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={formData.diamention}
                          onChange={(e) => setFormData({...formData, diamention: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Height</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g., 5cm"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={formData.height}
                          onChange={(e) => setFormData({...formData, height: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Material</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g., Natural"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.material}
                        onChange={(e) => setFormData({...formData, material: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">Product Variants</label>
                        <button
                          type="button"
                          onClick={addVariant}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                        >
                          Add Variant
                        </button>
                      </div>
                      {formData.variants.map((variant, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 mb-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">Variant {index + 1}</span>
                            {formData.variants.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeVariant(index)}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-500">Size</label>
                              <input
                                type="number"
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={variant.size}
                                onChange={(e) => updateVariant(index, 'size', parseInt(e.target.value) || 0)}
                                placeholder="Enter size"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500">
                                {editingProduct ? 'New Images (optional)' : 'Images'}
                              </label>
                              {editingProduct && editingProduct.variants?.[index]?.images?.length > 0 && (
                                <div className="mt-1 mb-2">
                                  <div className="flex gap-1 flex-wrap">
                                    {editingProduct.variants[index].images.map((image: string, imgIndex: number) => (
                                      <img
                                        key={imgIndex}
                                        className="w-8 h-8 object-cover rounded border"
                                        src={`https://rudraksh-api.volvrit.org/${image}`}
                                        alt={`V${index + 1}-${imgIndex + 1}`}
                                      />
                                    ))}
                                  </div>
                                  <p className="text-xs text-gray-400">Current variant images</p>
                                </div>
                              )}
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onChange={(e) => updateVariant(index, 'images', e.target.files)}
                              />
                              {editingProduct && (
                                <p className="text-xs text-gray-400 mt-1">Add more images</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {editingProduct ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Product Details Modal */}
      {showProductModal && selectedProduct && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseProductModal}></div>
            
            <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all max-w-4xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Product Details</h3>
                  <button type="button" onClick={handleCloseProductModal}>
                    <XMarkIcon className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedProduct.variants?.[0]?.images?.map((image: string, index: number) => (
                          <img
                            key={index}
                            className="w-full h-32 object-cover rounded-lg"
                            src={`https://rudraksh-api.volvrit.org/${image}`}
                            alt={`Product ${index + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-2xl font-bold text-gray-900">{selectedProduct.productname}</h4>
                        <p className="text-lg text-green-600 font-semibold">₹{selectedProduct.price}</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-bold text-gray-700">Description</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedProduct.description}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold text-gray-700">Category</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedProduct.categoryId?.categoryname || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-700">Rating</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedProduct.rating || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-700">Product Type</label>
                          <p className="mt-1 text-sm text-gray-900">{selectedProduct.producttype || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-700">Status</label>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            selectedProduct.isactive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {selectedProduct.isactive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {selectedProduct.variants && selectedProduct.variants.length > 0 && (
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Variants</label>
                      <div className="space-y-2">
                        {selectedProduct.variants.map((variant: any, index: number) => (
                          <div key={index} className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm"><span className="font-medium">Size:</span> {variant.size}</p>
                            <p className="text-sm"><span className="font-medium">Images:</span> {variant.images?.length || 0} images</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedProduct.feature && (
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Features</label>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Dimension</label>
                            <p className="text-sm text-gray-900">{selectedProduct.feature.diamention || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Height</label>
                            <p className="text-sm text-gray-900">{selectedProduct.feature.height || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500">Material</label>
                            <p className="text-sm text-gray-900">{selectedProduct.feature.material || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700">Created Date</label>
                      <p className="mt-1 text-sm text-gray-900">{new Date(selectedProduct.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700">Last Updated</label>
                      <p className="mt-1 text-sm text-gray-900">{new Date(selectedProduct.updatedAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleCloseProductModal}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;